import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createElement,
} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Divider, Icon } from 'semantic-ui-react'
import { DateTime } from 'luxon'
import { isFunction } from 'lodash'

import {
  useReselector,
  useFetchResponse,
  useScrollRef,
} from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFetchError, selectIsFetchingForKeys } from '@/reducers/fetch'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { selectActivePlaidFinancialAccounts } from '@/selectors/financeSelectors'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { fetchProvidersIfNeeded } from '@/features/provider/provider.actions'
import { getAllProviders } from '@/features/provider/provider.selectors'
import {
  getMissingStatements,
  MissingStatement,
  fetchUserDocuments,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import {
  selectUncategorizedTransactionsNotNeedClarification,
  selectUserTransactionsByCategoryIdentifierForYear,
  selectAssetsOver2500ForYear,
} from '@/features/transactions/transactions.selectors'
import { isUnclarifiedTransactionValid } from '@/features/transactions/helpers'
import { fetchFilteredUserTransactions } from '@/features/transactions/transactions.slice'
import { fetchTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import {
  TransactionCategoryIdentifier,
  Transaction,
} from '@/reducers/admin/allTransactions.slice'
import { TRANSACTION_CATEGORY_IDENTIFIERS } from '@/features/transactions/constants'
import { ProgressBarNoTitles, Text } from '@/componentsV1/BaseComponents'
import { NavButtons } from './nav-buttons'
import { DOCS_REVIEW_STEP_ID, IStep, IStepState, STEPS } from './step-map'
import {
  userGetBookkeepingStep,
  userGetProcessingFees,
  userSubmitBookkeepingModule,
  userUpdateBookkeepingStepContext,
  FETCH_USER_SUBMIT_BOOKKEEPING_MODULE_KEY,
  FETCH_USER_GET_BOOKKEEPING_STEP_KEY,
  FETCH_USER_UPDATE_BOOKKEEPING_STEP_CONTEXT_KEY,
} from './actions'
import { fetchPayrollProfileIfNeeded } from '@/features/payroll/payrollActions'
import {
  getFinancialProfile,
  selectIsCurrentUserScorp,
} from '@/selectors/user.selectors'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import {
  selectUserDocumentList,
  getUserTaxDocumentsForCategory,
} from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { selectUserDocumentCategoryByInternalName } from '@/features/admin/user-document-categories/userDocumentCategories.selectors'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { selectTaxUserDocuments } from '@/features/taxes/annual-taxes/taxUserDocuments.selector'
import { fetchTaxUserDocumentsIfNeeded } from '@/features/taxes/annual-taxes/taxUserDocuments.slice'
import './styles.scss'
import { logHeardError } from '@/utils/errorHelpers'
import {
  EndOfYearAdminReviewStep,
  StepContext,
} from '@/features/bookkeeping/end-of-year-review/types'
import { Alert } from '@/ui'
import { DocumentValidationProvider } from '../components/document-validation'

const DOCUMENT_STEP_IDS: string[] = [
  EndOfYearAdminReviewStep.docsAssetsOver2500,
  DOCS_REVIEW_STEP_ID,
]

interface IUnclarifiedTransactions {
  uncategorized: Transaction[]
  bookkeeperClarification: Transaction[]
}

export const EndOfYearReviewContainer = () => {
  const [userSteps, setUserSteps] = useState<IStep[]>([])
  const [isStepComplete, setIsStepComplete] = useState(true)
  const [isPreventCall, setIsPreventCall] = useState(false)
  const [stepContextsById, setStepContextsById] = useState<
    Record<string, StepContext>
  >({})
  const [missingStatements, setMissingStatements] =
    useState<MissingStatement[]>()
  const [isMissingStatements, setIsMissingStatements] = useState(false)
  const [form1120sMissing, setForm1120sMissing] = useState(false)
  const [hasSplitTransactions, setHasSplitTransactions] = useState(false)
  const [amountSplitTransaction, setAmountSplitTransaction] = useState(0)
  const [isOtherExpensesStepRequired, setIsOtherExpensesStepRequired] =
    useState(true)
  const [isOtherIncomeStepRequired, setIsOtherIncomeStepRequired] =
    useState(true)
  const [isOtherTransactionRequired, setIsOtherTransactionStepRequired] =
    useState(true)
  const [
    isInvestmentTransactionStepRequired,
    setIsInvestmentTransactionStepRequired,
  ] = useState(true)
  const [isOwnerDistributionStepRequired, setIsOwnerDistributionStepRequired] =
    useState(true)
  const [
    showUnclarifiedTransactionsError,
    setShowUnclarifiedTransactionsError,
  ] = useState(false)
  const [showDocumentsError, setShowDocumentsError] = useState(false)

  const { scrollRef, scrollToRef } = useScrollRef()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const year = useReselector(selectCurrentAnnualTaxYear)

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const currentStep = useMemo(() => {
    const stepRoute = location.pathname.match(/(?<=\/)[\w|-]+$/)?.[0]

    return userSteps.find((step) => step.route === stepRoute) || STEPS[0]
  }, [location.pathname, userSteps])

  const setReviewIndex = useCallback(
    (route: string) => {
      navigate(`/taxes/annual/complete-year-end-bookkeeping/${year}/${route}`)
    },
    [navigate, year]
  )

  useEffect(() => {
    dispatch(fetchTransactionCategoriesIfNeeded())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
    dispatch(fetchPayrollProfileIfNeeded())
    dispatch(fetchUserDocuments())
    dispatch(fetchTaxUserDocumentsIfNeeded())
    dispatch(fetchProvidersIfNeeded({ alwaysFetch: true }))
    dispatch(fetchFinancialAccountsIfNeeded())
    dispatch(fetchPlaidItemsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    dispatch(
      fetchFilteredUserTransactions({
        startDate: DateTime.fromObject({ year: parseInt(year) }).startOf(
          'year'
        ),
        endDate: DateTime.fromObject({ year: parseInt(year) }).endOf('year'),
      })
    )
  }, [dispatch, year])

  const financialAccounts = useReselector(selectActivePlaidFinancialAccounts)
  const financialProfile = useReselector(getFinancialProfile)

  const unclarifiedTransactions = useReselector(
    selectUncategorizedTransactionsNotNeedClarification,
    year
  )

  const otherExpenses = useReselector(
    selectUserTransactionsByCategoryIdentifierForYear,
    TransactionCategoryIdentifier.other_expenses,
    year
  )

  const otherIncome = useReselector(
    selectUserTransactionsByCategoryIdentifierForYear,
    TransactionCategoryIdentifier.other_income,
    year
  )

  const investmentTransactions = useReselector(
    selectUserTransactionsByCategoryIdentifierForYear,
    TransactionCategoryIdentifier.owners_investments,
    year
  )

  const distributionTransactions = useReselector(
    selectUserTransactionsByCategoryIdentifierForYear,
    TransactionCategoryIdentifier.owners_distribution,
    year
  )

  const categoryForm1120s = useReselector(
    selectUserDocumentCategoryByInternalName,
    UserDocumentCategoryIdentifier.finalReturnForm1120s
  )

  const taxUserDocuments = useReselector(selectTaxUserDocuments)
  const providers = useReselector(getAllProviders)
  const assetsOver2500 = useReselector(selectAssetsOver2500ForYear, year)

  const fetchingStatements = useFetchResponse(getMissingStatements)
  useEffect(() => {
    const filterStatementsByYear = (statements: string[], year: number) => {
      return statements.filter((statement) => {
        const statementDate = DateTime.fromISO(statement, { zone: 'utc' })
        return statementDate.year === year
      })
    }
    const statements = fetchingStatements || []

    const statementsFiltered = statements.map((statement) => ({
      ...statement,
      statements: filterStatementsByYear(statement.statements, parseInt(year)),
    }))

    const filteredAccount = []
    for (const statement of statementsFiltered) {
      if (statement.statements.length > 0) {
        filteredAccount.push(statement)
      }
    }
    const filteredStatements = filteredAccount

    setMissingStatements(filteredStatements)
    if (filteredStatements && filteredStatements?.length > 0) {
      setIsMissingStatements(true)
    }
  }, [fetchingStatements, year])

  useEffect(() => {
    if (otherExpenses.length > 0) {
      setIsOtherExpensesStepRequired(false)
    }
    if (otherIncome.length > 0) {
      setIsOtherIncomeStepRequired(false)
    }
    if (unclarifiedTransactions.length > 0) {
      setIsOtherTransactionStepRequired(false)
    }
    if (investmentTransactions.length > 0) {
      setIsInvestmentTransactionStepRequired(false)
    }
    if (distributionTransactions.length > 0) {
      setIsOwnerDistributionStepRequired(false)
    }
  }, [
    otherExpenses.length,
    otherIncome.length,
    unclarifiedTransactions.length,
    investmentTransactions.length,
    distributionTransactions.length,
  ])

  const processingFees = useFetchResponse(userGetProcessingFees, {
    year,
    transactionCategoryIdentifiers: [],
    includeProviders: true,
    incomeOnly: true,
  })

  const payrollAndContractorCategoryIdentifiers: string[] = [
    TRANSACTION_CATEGORY_IDENTIFIERS.payrollContractors,
    TRANSACTION_CATEGORY_IDENTIFIERS.payrollEmployeeBenefits,
    TRANSACTION_CATEGORY_IDENTIFIERS.payrollExpensesAndFees,
    TRANSACTION_CATEGORY_IDENTIFIERS.payrollPayable,
    TRANSACTION_CATEGORY_IDENTIFIERS.payrollReimbursableExpenses,
    TRANSACTION_CATEGORY_IDENTIFIERS.payrollSalariesAndWages,
    TRANSACTION_CATEGORY_IDENTIFIERS.payrollSutaExpense,
    TRANSACTION_CATEGORY_IDENTIFIERS.payrollTaxes,
    TRANSACTION_CATEGORY_IDENTIFIERS.payrollWorkersCompensation,
  ]

  const payrollAndContractorTxns = useFetchResponse(userGetProcessingFees, {
    year,
    transactionCategoryIdentifiers: payrollAndContractorCategoryIdentifiers,
  })

  const isSCorp = useReselector(selectIsCurrentUserScorp)

  const isMultiYearSCorp = useMemo(() => {
    if (!financialProfile) return false
    const { taxEntityType, scorpVerifiedAt, entityChangeElectionDate } =
      financialProfile
    if (
      taxEntityType !== TAX_ENTITY_TYPES.form_1120_s ||
      !scorpVerifiedAt ||
      !entityChangeElectionDate
    )
      return false
    const startOfYear = DateTime.fromObject(
      { year: parseInt(year), month: 1, day: 1 },
      { zone: 'utc' }
    )
    const electionDate = DateTime.fromISO(entityChangeElectionDate, {
      zone: 'utc',
    })
    return electionDate < startOfYear
  }, [financialProfile, year])

  const getUnclarifiedTransactions = (
    transactions: Transaction[]
  ): IUnclarifiedTransactions => {
    let amountSplitTransactions = 0
    let amountAllTransactions = 0
    let hasSplitTransaction = false
    const notCategoryTransactions = transactions.reduce(
      (acc, transaction) => {
        amountAllTransactions += 1
        if (transaction.type !== 'split') {
          acc.uncategorized.push(transaction)
        } else {
          amountSplitTransactions += 1
          hasSplitTransaction = true
        }
        return acc
      },
      {
        bookkeeperClarification: [] as Transaction[],
        uncategorized: [] as Transaction[],
      }
    )
    setAmountSplitTransaction(amountSplitTransactions)
    setHasSplitTransactions(hasSplitTransaction)
    if (
      amountSplitTransactions === amountAllTransactions &&
      hasSplitTransaction
    ) {
      setIsOtherTransactionStepRequired(true)
    }
    return notCategoryTransactions
  }

  const documents = useReselector(selectUserDocumentList)

  const yearNumber = parseInt(year, 10) // Convert string to number
  const lastTaxYear = yearNumber - 1 // Subtract 1
  const lastTaxYearString = lastTaxYear.toString()

  const isTaxCategoryDocs = useMemo(() => {
    const taxDocLength = getUserTaxDocumentsForCategory(
      categoryForm1120s,
      taxUserDocuments,
      documents,
      lastTaxYearString
    ).length
    if (
      taxDocLength === 0 &&
      documents.length > 0 &&
      taxUserDocuments.length > 0
    ) {
      setForm1120sMissing(true)
    }
    return taxDocLength === 0
  }, [categoryForm1120s, taxUserDocuments, documents, lastTaxYearString])

  const userBookkeepingStatus = useMemo(() => {
    return {
      financialAccounts,
      missingBankStatements: missingStatements,
      isMissingStatements,
      unclarifiedTransactions: getUnclarifiedTransactions(
        unclarifiedTransactions
      ),
      otherExpenses,
      otherIncome,
      isSCorp,
      investmentTransactions,
      distributionTransactions,
      payrollAndContractorTxns,
      processingFees,
      assetsOver2500,
      isMultiYearSCorp,
      documents,
      isTaxCategoryDocs,
      form1120sMissing,
    }
  }, [
    financialAccounts,
    unclarifiedTransactions,
    otherExpenses,
    otherIncome,
    isSCorp,
    isMissingStatements,
    investmentTransactions,
    distributionTransactions,
    payrollAndContractorTxns,
    missingStatements,
    processingFees,
    assetsOver2500,
    isMultiYearSCorp,
    documents,
    isTaxCategoryDocs,
    form1120sMissing,
  ])

  const stepState = useMemo<IStepState>(() => {
    return {
      lastEoyBookkeepingScreen: '/base/year/step_slug',
      providers,
      taxYear: year,
      isOtherExpensesStepRequired,
      isOtherIncomeStepRequired,
      isOtherTransactionRequired,
      isInvestmentTransactionStepRequired,
      isOwnerDistributionStepRequired,
      showUnclarifiedTransactionsError,
    }
  }, [
    providers,
    year,
    isOtherExpensesStepRequired,
    isOtherIncomeStepRequired,
    isOtherTransactionRequired,
    isInvestmentTransactionStepRequired,
    isOwnerDistributionStepRequired,
    showUnclarifiedTransactionsError,
  ])

  useEffect(() => {
    const steps = STEPS.filter((step) => {
      return step.isRequired(userBookkeepingStatus, stepState)
    })
    setUserSteps(steps)
  }, [userBookkeepingStatus, stepState])

  useEffect(() => {
    // Only set isStepComplete to the initial value based on isAlwaysComplete.
    // For steps where isAlwaysComplete is true, we set it to true.
    // For steps where isAlwaysComplete is false, we set it to false ONCE,
    // then the child component takes over via setIsStepComplete.
    if (!isPreventCall) {
      const isAlwaysComplete = currentStep.isAlwaysComplete
      if (isAlwaysComplete === true) {
        setIsStepComplete(true)
      }
      // Don't reset to false for isAlwaysComplete=false steps - let child manage it
    }
    if (currentStep.route === 'upload-missing-bank-statements') {
      if (missingStatements?.length === 0) {
        setIsStepComplete(true)
      }
    }
    if (currentStep.route === 'categorize-transactions') {
      if (unclarifiedTransactions?.length === 0) {
        setIsStepComplete(true)
      }
      if (
        hasSplitTransactions &&
        unclarifiedTransactions.length === amountSplitTransaction
      ) {
        setIsStepComplete(true)
      }
    }

    setIsPreventCall(false)
  }, [
    userSteps,
    isPreventCall,
    missingStatements?.length,
    unclarifiedTransactions,
    hasSplitTransactions,
    amountSplitTransaction,
    currentStep.route,
    currentStep.isAlwaysComplete,
  ])

  const prevStepUrl = useMemo(() => {
    const fromStepIndex = userSteps.findIndex(
      (step) => step.id === currentStep.id
    )

    return (
      (fromStepIndex !== 0
        ? userSteps[fromStepIndex - 1]?.route
        : 'taxes/annual') || 'taxes/annual'
    )
  }, [currentStep.id, userSteps])

  const nextStepUrl = useMemo(() => {
    const fromStepIndex = userSteps.findIndex(
      (step) => step.id === currentStep.id
    )

    return (
      (fromStepIndex !== userSteps.length - 1
        ? userSteps[fromStepIndex + 1]?.route
        : 'taxes/annual') || 'taxes/annual'
    )
  }, [currentStep.id, userSteps])

  useEffect(() => {
    const fetchStep = async (stepId: string | undefined) => {
      if (stepId) {
        const step = await userGetBookkeepingStep(stepId)(dispatch)
        setStepContextsById((prev) => ({
          ...prev,
          [stepId]: step?.context ?? {},
        }))
      }
    }
    fetchStep(currentStep.id)
  }, [currentStep.id, dispatch])

  const updateStepContext = useCallback(
    async (factor: number) => {
      const fromStepId = currentStep.id

      const fromStepIndex = userSteps.findIndex(
        (step) => step.id === fromStepId
      )

      const toStepId =
        fromStepIndex !== -1 ? userSteps[fromStepIndex + factor]?.id : undefined

      if (fromStepId && toStepId) {
        try {
          const updatedStep = await userUpdateBookkeepingStepContext(
            fromStepId,
            toStepId,
            stepContextsById[fromStepId]
          )(dispatch)

          if (updatedStep?.context) {
            setStepContextsById((prev) => ({
              ...prev,
              [fromStepId]: updatedStep.context,
            }))
          }

          return true
        } catch (error) {
          logHeardError(error)
          return false
        }
      }
      return true
    },
    [currentStep.id, userSteps, stepContextsById, dispatch]
  )

  type AutoSaveArgument =
    | StepContext
    | ((prevStepContext: StepContext | undefined) => StepContext)
  const autoSaveStepContext = useCallback(
    async (arg: AutoSaveArgument) => {
      const fromStepId = currentStep.id

      const newStepContext = isFunction(arg)
        ? arg(fromStepId ? stepContextsById[fromStepId] : undefined)
        : arg
      if (fromStepId) {
        const updatedStep = await userUpdateBookkeepingStepContext(
          fromStepId,
          fromStepId,
          newStepContext
        )(dispatch)
        if (updatedStep?.context) {
          setStepContextsById((prev) => ({
            ...prev,
            [fromStepId]: updatedStep.context,
          }))
        } else {
          setStepContextsById((prev) => ({
            ...prev,
            [fromStepId]: newStepContext,
          }))
        }
      }
    },
    [currentStep.id, stepContextsById, dispatch]
  )

  const handlePrevStep = useCallback(async () => {
    const success = await updateStepContext(-1)
    if (success) {
      if (prevStepUrl !== 'taxes/annual') {
        navigate(
          `/taxes/annual/complete-year-end-bookkeeping/${year}/${prevStepUrl}`
        )
      } else {
        navigate('/taxes/annual')
      }
    }
  }, [updateStepContext, prevStepUrl, year, navigate])

  const validateUnclarifiedTransactions = useCallback(() => {
    if (unclarifiedTransactions.length === 0) {
      return true
    }
    return unclarifiedTransactions.every(isUnclarifiedTransactionValid)
  }, [unclarifiedTransactions])

  const handleNextStep = useCallback(async () => {
    // Check validation for unclarified transactions step
    const currentStepId = currentStep.id
    if (currentStepId === 'unclarified-transactions') {
      const isValid = validateUnclarifiedTransactions()
      if (!isValid) {
        setShowUnclarifiedTransactionsError(true)
        scrollToRef()
        return
      }
      // Hide error banner if validation passes
      setShowUnclarifiedTransactionsError(false)
    }

    if (DOCUMENT_STEP_IDS.includes(currentStepId ?? '')) {
      if (!isStepComplete) {
        setShowDocumentsError(true)
        scrollToRef()
        return
      }
      setShowDocumentsError(false)
    }

    const stepContextUpdated = await updateStepContext(1)

    // Submit the bookkeeping module if the current step is a submit step
    let submittedOrSkipped = true
    if (currentStep.isSubmit && stepContextUpdated) {
      submittedOrSkipped = await userSubmitBookkeepingModule()(dispatch)
      // If submission failed, don't navigate
      if (!submittedOrSkipped) {
        return
      }
    }

    if (stepContextUpdated && submittedOrSkipped) {
      if (nextStepUrl !== 'taxes/annual') {
        navigate(
          `/taxes/annual/complete-year-end-bookkeeping/${year}/${nextStepUrl}`
        )
      } else {
        navigate('/taxes/annual')
      }
    }
  }, [
    currentStep.id,
    currentStep.isSubmit,
    updateStepContext,
    validateUnclarifiedTransactions,
    isStepComplete,
    scrollToRef,
    dispatch,
    nextStepUrl,
    navigate,
    year,
  ])

  // Hide error banner when transactions become valid
  useEffect(() => {
    if (showUnclarifiedTransactionsError) {
      const isValid = validateUnclarifiedTransactions()
      if (isValid) {
        setShowUnclarifiedTransactionsError(false)
      }
    }
  }, [
    showUnclarifiedTransactionsError,
    validateUnclarifiedTransactions,
    unclarifiedTransactions,
  ])

  useEffect(() => {
    if (showDocumentsError && isStepComplete) {
      setShowDocumentsError(false)
    }
  }, [showDocumentsError, isStepComplete])

  useEffect(() => {
    setShowDocumentsError(false)
    wrapperRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' })
  }, [currentStep.id])

  const handleSaveAndExit = useCallback(async () => {
    const success = await updateStepContext(0)
    if (success) {
      navigate('/taxes/annual')
    }
  }, [navigate, updateStepContext])

  const setIsStepCompleteFun = useCallback(
    async (isComplete: boolean, isMissingBank = false) => {
      if (isMissingBank) {
        setIsPreventCall(true)
        const filterStatementsByYear = (statements: string[], year: number) => {
          return statements.filter((statement) => {
            const statementDate = DateTime.fromISO(statement, { zone: 'utc' })
            return statementDate.year === year
          })
        }
        const fetchingStatements =
          (await getMissingStatements()(dispatch)) || []
        const statementsFiltered = fetchingStatements.map((statement) => ({
          ...statement,
          statements: filterStatementsByYear(
            statement.statements,
            parseInt(year)
          ),
        }))
        const filteredAccount = []
        for (const statement of statementsFiltered) {
          if (statement.statements.length > 0) {
            filteredAccount.push(statement)
          }
        }
        const filteredStatements = filteredAccount
        setMissingStatements(filteredStatements)
        if (filteredStatements?.length === 0) {
          setIsStepComplete(true)
        }
      } else {
        setIsStepComplete(isComplete)
      }
    },
    [dispatch, year]
  )

  const getCurrentProps = () => {
    if (currentStep.route === 'first-submission') {
      return {
        userSteps,
        setReviewIndex,
        setIsStepComplete,
      }
    }

    return {
      ...(currentStep.props?.(userBookkeepingStatus, stepState) || {}),
    }
  }

  const hideWrapper = currentStep.shouldHideWrapper || false

  const isLoading = useReselector(selectIsFetchingForKeys, [
    FETCH_USER_SUBMIT_BOOKKEEPING_MODULE_KEY,
    FETCH_USER_GET_BOOKKEEPING_STEP_KEY,
    FETCH_USER_UPDATE_BOOKKEEPING_STEP_CONTEXT_KEY,
  ])

  const submitError = useReselector(
    getFetchError,
    FETCH_USER_SUBMIT_BOOKKEEPING_MODULE_KEY
  )

  return (
    <div className="eoy-review_wrapper" ref={wrapperRef}>
      {!hideWrapper && (
        <>
          <div className="header">
            <Text>{year} End of Year Review</Text>
            <div className="autosave-wrapper">
              <Icon name="check" style={{ color: '#457634' }} />
              <Text style={{ color: '#457634' }}>Autosaved</Text>
            </div>
          </div>

          <ProgressBarNoTitles
            currentStep={userSteps.findIndex(
              (step) => step.id === currentStep.id
            )}
            totalSteps={userSteps.length}
          />
        </>
      )}

      <DocumentValidationProvider showErrors={showDocumentsError}>
        <div className="step-body">
          <span ref={scrollRef} />
          {/* @ts-expect-error temp type issue resolution - ticket hea-5043 */}
          {createElement(currentStep.component, {
            ...getCurrentProps(),
            ...{
              stepContext:
                (currentStep.id
                  ? stepContextsById[currentStep.id]
                  : undefined) || {},
              setStepContext: autoSaveStepContext,
              setIsStepComplete: currentStep.isAlwaysComplete
                ? () => {}
                : setIsStepCompleteFun,
            },
          })}
        </div>
      </DocumentValidationProvider>

      {!hideWrapper && (
        <>
          <Divider style={{ margin: '0 0 24px 0' }} />
          {submitError && (
            <Alert title="Error submitting books" variant="error">
              {submitError.message}
            </Alert>
          )}

          <NavButtons
            isStepComplete={
              isStepComplete || DOCUMENT_STEP_IDS.includes(currentStep.id ?? '')
            }
            stepBack={handlePrevStep}
            stepForward={handleNextStep}
            saveAndExit={handleSaveAndExit}
            isSubmit={currentStep.isSubmit}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  )
}
export default EndOfYearReviewContainer
